import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-on-disposition-sign-event',
  templateUrl: './on-disposition-sign-event.component.html',
  styleUrls: ['./on-disposition-sign-event.component.scss']
})
export class OnDispositionSignEventComponent implements OnInit {
  outcome: 'signed' | 'expired' | 'cancelled' | 'declined' | 'failed' | 'unknown' = 'unknown';
  regenerating = false;
  regenerateError = false;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const mission = params['mission'];
      const candidate = params['candidate'];
      const event = params['event']; // DocuSign appends this to the returnUrl
      const alreadyRegenerated = params['regenerated'] === '1';

      this.outcome = this.classify(event);

      // No `event` param means the page was opened from a Collary entry point (chat
      // button, email "Signer" link, etc.) rather than as a DocuSign returnUrl.
      // DocuSign embedded URLs are single-use, so we mint a fresh one on EVERY entry —
      // otherwise the establishment's second click hits "Invalid Managed Token ID".
      // Set outcome to 'expired' so the existing 'expired' switch branch (gated on
      // regenerateError) drives the UI consistently if regen fails.
      if (!event && mission && candidate && !alreadyRegenerated) {
        this.outcome = 'expired';
        this.regenerateAndRedirect(mission, candidate);
        return;
      }

      // CRITICAL: only mark the disposition contract as signed when DocuSign reported
      // signing_complete. ttl_expired / session_timeout / cancel / decline all hit the
      // same returnUrl but mean the establishment did NOT sign.
      if (this.outcome === 'signed') {
        if (mission && candidate) {
          this.http.get(`${environment.api.BASE_URL}/docusign/disposition-event?missionId=${mission}&candidatId=${candidate}`)
            .subscribe({
              next: () => {
                console.log('Disposition signing event processed successfully');
                setTimeout(() => window.close(), 20000);
              },
              error: (error) => console.error('Error processing disposition signing event:', error)
            });
        }
        return;
      }

      // Auto-regenerate a fresh sign URL when the embedded session timed out.
      if (this.outcome === 'expired' && mission && candidate && !alreadyRegenerated) {
        this.regenerateAndRedirect(mission, candidate);
        return;
      }

      console.warn('DocuSign returnUrl event=', event, '— no action taken.');
    });
  }

  private regenerateAndRedirect(missionId: string, candidatId: string): void {
    this.regenerating = true;
    this.regenerateError = false;
    const url = `${environment.api.BASE_URL}/docusign/regenerate-disposition-sign-url`
      + `?missionId=${missionId}&candidatId=${candidatId}`;
    this.http.get<{ url: string }>(url).subscribe({
      next: (res) => {
        if (res && res.url) {
          window.location.replace(res.url);
        } else {
          this.regenerating = false;
          this.regenerateError = true;
        }
      },
      error: (err) => {
        console.error('Failed to regenerate disposition sign URL:', err);
        this.regenerating = false;
        this.regenerateError = true;
      }
    });
  }

  private classify(event: string | undefined): 'signed' | 'expired' | 'cancelled' | 'declined' | 'failed' | 'unknown' {
    switch (event) {
      case 'signing_complete': return 'signed';
      case 'ttl_expired':
      case 'session_timeout': return 'expired';
      case 'cancel': return 'cancelled';
      case 'decline': return 'declined';
      case 'exception':
      case 'access_code_failed':
      case 'id_check_failed':
      case 'fax_pending': return 'failed';
      default: return 'unknown';
    }
  }
}
